import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { WHY_SYSTEM_PAGE } from '../../data/whySystemPage';
import './Why.css';

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function TechnicalPlate({ fig, figBold, image, alt, className = '', showFig = true }) {
  return (
    <div className={`plate ${className}`.trim()}>
      <span className="corner tl" />
      <span className="corner tr" />
      <span className="corner bl" />
      <span className="corner br" />
      {showFig ? (
        <span className="fig">
          {fig} — <b>{figBold}</b>
        </span>
      ) : null}
      <img src={image} alt={alt} loading="lazy" decoding="async" />
    </div>
  );
}

function useReveal() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const els = Array.from(root.querySelectorAll('.reveal'));
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
    );

    els.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < (window.innerHeight || 800) * 0.95) el.classList.add('in');
      io.observe(el);
    });

    const fallback = window.setTimeout(() => els.forEach((el) => el.classList.add('in')), 2500);
    return () => {
      window.clearTimeout(fallback);
      io.disconnect();
    };
  }, []);

  return rootRef;
}

function FnIcon({ id }) {
  const icons = {
    plan: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="1.5"/>
        <path d="M3 9h18M9 21V9"/>
        <path d="M13 13h4M13 17h4"/>
      </svg>
    ),
    frame: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="2" width="9" height="9" rx="1"/>
        <rect x="13" y="2" width="9" height="9" rx="1"/>
        <rect x="2" y="13" width="9" height="9" rx="1"/>
        <rect x="13" y="13" width="9" height="9" rx="1"/>
      </svg>
    ),
    sheath: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="5" width="20" height="4" rx="1"/>
        <rect x="2" y="11" width="20" height="4" rx="1"/>
        <rect x="2" y="17" width="20" height="4" rx="1"/>
      </svg>
    ),
    mep: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3-3a1 1 0 0 0 0-1.4l-1.6-1.6a1 1 0 0 0-1.4 0l-3 3z"/>
        <path d="M13 9l-9.4 9.4a2 2 0 0 0 0 2.8l.2.2a2 2 0 0 0 2.8 0L16 12"/>
      </svg>
    ),
    rework: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 6L9 17l-5-5"/>
      </svg>
    ),
  };
  return <span className="fn-icon">{icons[id] || null}</span>;
}

function WhyTestimonials({ testimonials }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = testimonials.items.length;
  const current = testimonials.items[active];

  const goTo = (index) => {
    setActive((index + count) % count);
  };

  useEffect(() => {
    if (paused || count <= 1) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;

    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % count);
    }, 6000);

    return () => window.clearInterval(timer);
  }, [paused, count]);

  return (
    <section
      className="testimonials"
      id="testimonials"
      aria-label="Customer testimonials"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <div className="wrap">
        <div className="sechead reveal">
          <div className="kicker">{testimonials.kicker}</div>
          <h2 className="h2">
            {testimonials.title} <span className="rust">{testimonials.titleAccent}</span>
          </h2>
        </div>

        <figure className="testimonials__card reveal" data-d="1">
          {current.featured ? (
            <span className="testimonials__badge">
              <span className="dot" aria-hidden="true" />
              {current.featuredLabel || 'Featured builder'}
            </span>
          ) : null}
          <span className="testimonials__mark" aria-hidden="true">
            “
          </span>
          <blockquote className="testimonials__quote">{current.quote}</blockquote>
          <figcaption className="testimonials__meta">
            <div>
              <p className="testimonials__author">{current.author}</p>
              <p className="testimonials__company">
                {current.href && current.partnerName ? (
                  <>
                    <Link to={current.href}>{current.partnerName}</Link>
                    {current.company ? ` · ${current.company}` : null}
                  </>
                ) : (
                  current.company
                )}
              </p>
            </div>
            <div className="testimonials__controls" role="group" aria-label="Testimonial navigation">
              <button
                type="button"
                className="testimonials__nav"
                aria-label="Previous testimonial"
                onClick={() => goTo(active - 1)}
              >
                <ArrowIcon />
              </button>
              <div className="testimonials__dots" role="tablist" aria-label="Testimonials">
                {testimonials.items.map((item, index) => (
                  <button
                    key={item.href || `${item.author}-${index}`}
                    type="button"
                    role="tab"
                    aria-selected={index === active}
                    aria-label={`Show testimonial ${index + 1} of ${testimonials.items.length}`}
                    className={index === active ? 'is-active' : undefined}
                    onClick={() => goTo(index)}
                  />
                ))}
              </div>
              <button
                type="button"
                className="testimonials__nav testimonials__nav--next"
                aria-label="Next testimonial"
                onClick={() => goTo(active + 1)}
              >
                <ArrowIcon />
              </button>
            </div>
          </figcaption>
        </figure>
      </div>
    </section>
  );
}

export default function Why() {
  const pageRef = useReveal();
  const [activePart, setActivePart] = useState(null);
  const stageRef = useRef(null);

  const {
    meta,
    sectionNav,
    hero,
    testimonials,
    systemsGallery,
    digitalFabrication,
    whyNow,
    proofBar,
    shift,
    pipeline,
    fieldNotes,
    anatomy,
    performance,
    finish,
    liveProject,
    cta,
  } = WHY_SYSTEM_PAGE;

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !('IntersectionObserver' in window)) return undefined;

    let seen = false;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !seen) {
            seen = true;
            setActivePart('wall');
            window.setTimeout(() => setActivePart(null), 1400);
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(stage);
    return () => io.disconnect();
  }, []);

  return (
    <div className="why-system-page grid-bg" ref={pageRef}>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
      </Helmet>

      <nav className="why-section-nav" aria-label="Page sections">
        <div className="why-section-nav__track">
          {sectionNav.map((item) => (
            <a key={item.href} href={item.href}>
              {item.label}
            </a>
          ))}
        </div>
      </nav>

      {/* ── Backyard launch announcement ── */}
      <div className="why-announce">
        <div className="wrap">
          <div className="why-announce__inner">
            <div className="why-announce__left">
              <span className="why-announce__badge">Now in Austin</span>
              <p className="why-announce__copy">
                <strong>Calling Central Texas homeowners —</strong>{' '}
                Construx is now collaborating with central Texas builders to deliver premium ADUs, backyard offices &amp; flex spaces in record times. See what's possible in your backyard today.
              </p>
            </div>
            <a
              className="why-announce__cta"
              href="https://austinadupro.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Get a free quote
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      <header className="hero" id="top">
        <div className="wrap">
          <div className="inner">
            <div>
              <div className="kicker reveal">{hero.kicker}</div>
              <h1 className="display reveal" data-d="1">
                {hero.titleAccent ? (
                  <>
                    {hero.title} <span className="rust">{hero.titleAccent}</span>
                  </>
                ) : (
                  hero.title
                )}
              </h1>
              <p className="lede reveal" data-d="2">
                {hero.lede}
              </p>
              <div className="cta-row reveal" data-d="3">
                <a className="btn btn-primary lg" href="#how">
                  See how it works <ArrowIcon />
                </a>
                <a className="btn btn-ghost lg" href="#anatomy">
                  Anatomy of the system
                </a>
              </div>
              <div className="specstrip reveal" data-d="3">
                {hero.specs.map((spec) => (
                  <div className="s" key={spec.label}>
                    <b>
                      {spec.value}
                      {spec.unit ? <span style={{ fontSize: '13px' }}>{spec.unit}</span> : null}
                    </b>
                    <span>{spec.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="heroplate reveal" data-d="2">
              <div className="heroplate-media">
                <TechnicalPlate image={hero.image} alt={hero.imageAlt} showFig={false} />
                <span className="hcall c1">
                  <span className="dot" />
                  {hero.callouts[0]}
                </span>
                <span className="hcall c2">
                  <span className="dot" />
                  {hero.callouts[1]}
                </span>
                <span className="hcall c3">
                  <span className="dot" />
                  {hero.callouts[2]}
                </span>
              </div>
              <div className="dimwrap">
                <div className="dim h">
                  <span>ENGINEERED FROM DIGITAL MODEL · ASSEMBLED ON SITE</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="systems-gallery" aria-label="Construx system components">
        <div className="wrap">
          <div className="systems-gallery__grid">
            {systemsGallery.items.map((item) => (
              <figure className="systems-gallery__item reveal" key={item.name}>
                <img
                  src={item.src}
                  alt={item.alt}
                  loading="lazy"
                  decoding="async"
                  style={item.objectPosition ? { objectPosition: item.objectPosition } : undefined}
                />
                <figcaption className="systems-gallery__label">
                  <span className="dot" aria-hidden="true" />
                  {item.name}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Digital Fabrication section hidden for now
      <section className="sec digifab" id="fabrication">
        <div className="wrap">
          <h2 className="digifab__title reveal">
            {digitalFabrication.title}{' '}
            <span className="digifab__accent">{digitalFabrication.titleAccent}</span>
          </h2>
          <div className="digifab__grid">
            {digitalFabrication.steps.map((step, index) => (
              <article className="digifab__step reveal" data-d={index % 2} key={step.title}>
                <div className="digifab__copy">
                  <span className="digifab__no">{step.no}</span>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </div>
                {step.image ? (
                  <div className={`digifab__media${step.mediaClass ? ` ${step.mediaClass}` : ''}`}>
                    <img src={step.image} alt={step.alt} loading="lazy" decoding="async" />
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </div>
      </section>
      */}

      <section className="proofbar">
        <div className="wrap row">
          <span className="tag">{proofBar.tag}</span>
          <div className="ticks">
            {proofBar.ticks.map((tick) => (
              <span key={tick}>{tick}</span>
            ))}
          </div>
        </div>
      </section>

      <WhyTestimonials testimonials={testimonials} />

      <section className="whynow" id="why-now">
        <div className="whynow__panel">
          <div
            className="whynow__pattern"
            style={{ backgroundImage: `url("${whyNow.pattern}")` }}
            aria-hidden="true"
          />
          <div className="wrap">
            <h2 className="whynow__title reveal">{whyNow.title}</h2>
            <div className="whynow__grid">
              {whyNow.columns.map((column, index) => (
                <article className="whynow__col reveal" data-d={index % 2} key={column.no}>
                  <span className="whynow__ghost" aria-hidden="true">
                    {column.no}
                  </span>
                  <div className="whynow__copy">
                    {column.lines.map((line) => (
                      <p key={line.text} className={line.bold ? 'is-bold' : undefined}>
                        {line.text}
                      </p>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="sec shift grid-bg" id="shift">
        <div className="wrap">
          <div className="sechead reveal">
            <div className="kicker">{shift.kicker}</div>
            <h2 className="h2">
              {shift.title} <span className="rust">{shift.titleAccent}</span>
            </h2>
            <p className="lede">{shift.lede}</p>
          </div>
          <div className="cmpgrid reveal">
            <div className="cmpcol old">
              <div className="tag">
                <span className="nm">{shift.legacy.tag}</span>
                <span className="badge">{shift.legacy.badge}</span>
              </div>
              <h3>{shift.legacy.headline}</h3>
              <div className="clist">
                {shift.legacy.items.map((item) => (
                  <div className="ci" key={item.bold}>
                    <XIcon />
                    <div>
                      <b>{item.bold}</b> {item.text}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="cmpcol new">
              <div className="tag">
                <span className="nm">{shift.construx.tag}</span>
                <span className="badge">{shift.construx.badge}</span>
              </div>
              <h3>{shift.construx.headline}</h3>
              <div className="clist">
                {shift.construx.items.map((item) => (
                  <div className="ci" key={item.bold}>
                    <CheckIcon />
                    <div>
                      <b>{item.bold}</b> {item.text}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="sec how grid-bg" id="how">
        <div className="wrap">
          <div className="sechead reveal">
            <div className="kicker">{pipeline.kicker}</div>
            <h2 className="h2">
              {pipeline.title} <span className="rust">{pipeline.titleAccent}</span>
            </h2>
            <p className="lede">{pipeline.lede}</p>
          </div>
          <div className="pipe">
            {pipeline.steps.map((step, index) => (
              <div className="pstep reveal" data-d={index} key={step.no}>
                {step.href ? (
                  <a
                    className="pic"
                    href={step.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ backgroundImage: `url("${step.image}")` }}
                    aria-label={step.alt || step.title}
                  >
                    <span className="no">{step.no}</span>
                  </a>
                ) : (
                  <div className="pic" style={{ backgroundImage: `url("${step.image}")` }}>
                    <span className="no">{step.no}</span>
                  </div>
                )}
                <div className="tx">
                  <span className="ph2">{step.phase}</span>
                  <b>
                    {step.title.split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <br />}
                        {line}
                      </React.Fragment>
                    ))}
                  </b>
                  <p>{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sec fieldnotes" id="field-notes">
        <div className="wrap">
          <div className="fn-head reveal">
            <div className="kicker">{fieldNotes.kicker}</div>
            <h2 className="h2">
              {fieldNotes.title} <span className="rust">{fieldNotes.titleAccent}</span>
            </h2>
          </div>
          <div className="fn-table reveal" data-d="1">
            <div className="fn-thead">
              <div className="fn-col-phase">Phase · Trade</div>
              <div className="fn-col-bars">
                <span className="fn-label-conv">Conventional</span>
                <span className="fn-label-cx">Construx</span>
              </div>
            </div>
            {fieldNotes.rows.map((row) => (
              <div className="fn-row" key={row.id}>
                <div className="fn-col-phase">
                  <FnIcon id={row.id} />
                  <div className="fn-phase-copy">
                    <b>{row.phase}</b>
                    <span>{row.trade}</span>
                  </div>
                </div>
                <div className="fn-col-bars">
                  <div className="fn-bar-wrap">
                    <div className="fn-bar fn-bar--conv" style={{ width: `${row.conventional.pct * 3}px` }} />
                    <span className="fn-bar-label">{row.conventional.label}</span>
                  </div>
                  <div className="fn-bar-wrap">
                    {row.construx.eliminated ? (
                      <div className="fn-eliminated">
                        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M13 3L3 13M3 3l10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                        <span>{row.construx.label}</span>
                      </div>
                    ) : (
                      <>
                        <div className="fn-bar fn-bar--cx" style={{ width: `${row.construx.pct * 3}px` }} />
                        <span className="fn-bar-label">{row.construx.label}</span>
                      </>
                    )}
                  </div>
                  <p className="fn-note">{row.note}</p>
                </div>
              </div>
            ))}
          </div>
          <figure className="fn-quote reveal" data-d="2">
            <svg className="fn-quote-mark" viewBox="0 0 32 24" fill="none" aria-hidden="true">
              <path d="M0 24V14.4C0 6.4 4.8 1.6 14.4 0l1.6 2.4C10.4 3.6 7.2 6.4 6.4 10.4H12V24H0zm18 0V14.4C18 6.4 22.8 1.6 32.4 0L34 2.4C28.4 3.6 25.2 6.4 24.4 10.4H30V24H18z" fill="currentColor"/>
            </svg>
            <blockquote>{fieldNotes.quote}</blockquote>
            <figcaption>{fieldNotes.quoteAuthor}</figcaption>
          </figure>
          <p className="fn-source">{fieldNotes.source}</p>
        </div>
      </section>

      <section className="sec anatomy grid-bg" id="anatomy">
        <div className="wrap">
          <div className="sechead reveal">
            <div className="kicker">{anatomy.kicker}</div>
            <h2 className="h2">
              {anatomy.title} <span className="rust">{anatomy.titleAccent}</span>
            </h2>
            <p className="lede">{anatomy.lede}</p>
          </div>
          <div className="anlayout">
            <div className="anstage reveal" ref={stageRef}>
              <TechnicalPlate image={anatomy.image} alt={anatomy.imageAlt} showFig={false} />
              {anatomy.parts.map((part) => (
                <React.Fragment key={part.id}>
                  <div
                    className={`hot${activePart === part.id ? ' on' : ''}`}
                    data-h={part.id}
                    style={part.hot}
                    aria-hidden="true"
                  />
                  <div
                    className={`hotlabel${activePart === part.id ? ' on' : ''}`}
                    data-h={part.id}
                    style={part.label}
                  >
                    {part.labelText}
                  </div>
                </React.Fragment>
              ))}
            </div>
            <div className="anlist reveal" data-d="1">
              {anatomy.parts.map((part) => (
                <div
                  className={`anrow${activePart === part.id ? ' active' : ''}`}
                  data-h={part.id}
                  key={part.id}
                  onMouseEnter={() => setActivePart(part.id)}
                  onMouseLeave={() => setActivePart(null)}
                >
                  <span className="pn">{part.code}</span>
                  <div className="info">
                    <b>{part.title}</b>
                    <p>{part.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="sec perf" id="perf">
        <div className="wrap">
          <div className="sechead reveal">
            <div className="kicker">{performance.kicker}</div>
            <h2 className="h2">{performance.title}</h2>
            <p className="lede">{performance.lede}</p>
          </div>
          <div className="spectable reveal">
            <div className="shead">
              <div>Metric</div>
              <div>Conventional</div>
              <div>Construx</div>
            </div>
            {performance.rows.map((row) => (
              <div className="srow" key={row.metric}>
                <div className="metric">
                  <b>{row.metric}</b>
                  <span>{row.sub}</span>
                </div>
                <div className="old">{row.conventional}</div>
                <div className="cx">{row.construx}</div>
              </div>
            ))}
          </div>
          <p className="specnote">{performance.note}</p>
        </div>
      </section>

      <section className="sec finish grid-bg" id="finish">
        <div className="wrap">
          <div className="sechead reveal">
            <div className="kicker">{finish.kicker}</div>
            <h2 className="h2">
              {finish.title} <span className="rust">{finish.titleAccent}</span> {finish.titleEnd}
            </h2>
            <p className="lede">{finish.lede}</p>
          </div>
          <div className="lead-img reveal">
            <TechnicalPlate fig="FIG.03" figBold="FINISHED BUILD" image={finish.leadImage} alt={finish.leadAlt} />
          </div>
          <div className="finishgrid reveal" data-d="1">
            {finish.grid.map((item) => (
              <figure key={item.caption}>
                <img src={item.src} alt={item.alt} loading="lazy" decoding="async" />
                <figcaption>{item.caption}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="sec liveproject" id="live-project">
        <div className="wrap">
          <div className="lp-head reveal">
            <div className="kicker">{liveProject.kicker}</div>
            <h2 className="h2">Live Build, <span className="rust">Finished ADU.</span></h2>
          </div>
          <div className="lp-grid reveal" data-d="1">
            {liveProject.photos.map((photo, i) => (
              <figure className="lp-cell" key={i}>
                <img src={photo.src} alt={photo.alt} loading="lazy" decoding="async" />
                <span className="lp-badge" aria-hidden="true">CX BUILD</span>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="sec cta grid-bg">
        <div className="wrap">
          <div className="sechead center reveal">
            <div className="kicker plain" style={{ justifyContent: 'center' }}>
              {cta.kicker}
            </div>
            <h2 className="h2">
              {cta.title} <span className="rust">{cta.titleAccent}</span>
            </h2>
          </div>
          <div className="ctagrid">
            {cta.cards.map((card, index) => (
              <div className={`ctacard${card.featured ? ' feat' : ''} reveal`} data-d={index} key={card.tag}>
                <span className="tag">{card.tag}</span>
                <h3>{card.title}</h3>
                <p>{card.body}</p>
                <div className="act">
                  <Link className="btn btn-primary lg" to={card.href}>
                    {card.label} <ArrowIcon />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
